import type {
  AITenantUsage,
  AIUsageOverview,
  AIUsageWindow,
  ToolCallCounts,
} from '@/types/control/ai-usage';

import { controlApiRequest } from './control-api-client';

/**
 * AI usage on control-api's admin surface.
 *
 * Two things shape this file.
 *
 * 1. **The unwired response is a `200`, not an error**, and it is missing fields
 *    rather than zeroing them. When the AI session projectors are not
 *    configured the body is the six totals at zero, `tenants: []` and
 *    `status: "projectors_not_configured"` — with no `tool_calls_by_name`,
 *    `start_date` or `end_date` at all. Branching on `status` before reading
 *    anything is the only way to tell that apart from an estate that genuinely
 *    has no AI usage.
 * 2. **`last_activity_at` is a non-pointer `time.Time`**, so a tenant with no
 *    activity serialises as the zero time rather than null. Left as-is it dates
 *    the tenant to the year 1.
 *
 * `GET /ai/usage/{tenantId}` is deliberately not wrapped: the aggregate already
 * carries a row per tenant, so the per-tenant endpoint would be a second call
 * for data in hand. It also accepts `limit` and `page_state` and reads neither.
 */

const BASE = '/api/admin/ai/usage';

const PROJECTORS_NOT_CONFIGURED = 'projectors_not_configured';

/** The Go zero time, which is what an inactive tenant's stamp arrives as. */
const ZERO_TIME_PREFIX = '0001-01-01';

type TenantUsageResponse = {
  tenant_id?: unknown;
  tenant_name?: unknown;
  total_sessions?: unknown;
  total_interactions?: unknown;
  total_tool_calls?: unknown;
  total_input_tokens?: unknown;
  total_output_tokens?: unknown;
  total_tokens?: unknown;
  tool_calls_by_name?: unknown;
  last_activity_at?: unknown;
};

type UsageResponse = {
  status?: unknown;
  total_sessions?: unknown;
  total_interactions?: unknown;
  total_tool_calls?: unknown;
  total_input_tokens?: unknown;
  total_output_tokens?: unknown;
  total_tokens?: unknown;
  tool_calls_by_name?: unknown;
  tenants?: unknown;
  start_date?: unknown;
  end_date?: unknown;
};

function asCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Null for an absent, non-string or zero-time stamp. */
function asStamp(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') {
    return null;
  }
  return value.startsWith(ZERO_TIME_PREFIX) ? null : value;
}

function asToolCalls(value: unknown): ToolCallCounts {
  if (typeof value !== 'object' || value === null) {
    return {};
  }

  const counts: ToolCallCounts = {};
  for (const [name, raw] of Object.entries(value as Record<string, unknown>)) {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      counts[name] = raw;
    }
  }
  return counts;
}

function toTenantUsage(res: TenantUsageResponse): AITenantUsage {
  const id = asString(res.tenant_id);

  return {
    tenantId: id,
    // `tenant_name` is omitempty and only resolved when the tenant list read
    // succeeds, so the id is the fallback rather than an empty cell.
    tenantName: asString(res.tenant_name) || id,
    totalSessions: asCount(res.total_sessions),
    totalInteractions: asCount(res.total_interactions),
    totalToolCalls: asCount(res.total_tool_calls),
    totalInputTokens: asCount(res.total_input_tokens),
    totalOutputTokens: asCount(res.total_output_tokens),
    totalTokens: asCount(res.total_tokens),
    toolCallsByName: asToolCalls(res.tool_calls_by_name),
    lastActivityAt: asStamp(res.last_activity_at),
  };
}

/** Blank bounds are dropped: an absent side means unbounded. */
function windowQuery(
  window: AIUsageWindow | undefined
): Record<string, string> {
  const query: Record<string, string> = {};

  if (window?.startDate) {
    query.start_date = window.startDate;
  }
  if (window?.endDate) {
    query.end_date = window.endDate;
  }

  return query;
}

export const aiUsageService = {
  async getOverview(
    window?: AIUsageWindow,
    signal?: AbortSignal
  ): Promise<AIUsageOverview> {
    const res = await controlApiRequest<UsageResponse | null>({
      path: BASE,
      query: windowQuery(window),
      signal,
    });

    const projectorsConfigured =
      asString(res?.status) !== PROJECTORS_NOT_CONFIGURED;

    const tenants = Array.isArray(res?.tenants)
      ? (res.tenants as TenantUsageResponse[]).map(toTenantUsage)
      : [];

    return {
      projectorsConfigured,
      totalSessions: asCount(res?.total_sessions),
      totalInteractions: asCount(res?.total_interactions),
      totalToolCalls: asCount(res?.total_tool_calls),
      totalInputTokens: asCount(res?.total_input_tokens),
      totalOutputTokens: asCount(res?.total_output_tokens),
      totalTokens: asCount(res?.total_tokens),
      toolCallsByName: asToolCalls(res?.tool_calls_by_name),
      tenants,
      startDate: asStamp(res?.start_date),
      endDate: asStamp(res?.end_date),
    };
  },
};
